import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * Recording notice. Consent is also captured in the WhatsApp invite, but the
 * in-call badge is the part people actually see, so it reflects LiveKit's own
 * isRecording flag rather than what we hoped we started.
 */
export function RecordingBadge({ expected }: { expected: boolean }) {
  const room = useRoomContext();
  const [isRecording, setIsRecording] = useState(room.isRecording);

  useEffect(() => {
    const onChanged = () => setIsRecording(room.isRecording);
    room.on(RoomEvent.RecordingStatusChanged, onChanged);
    onChanged();
    return () => {
      room.off(RoomEvent.RecordingStatusChanged, onChanged);
    };
  }, [room]);

  if (!expected && !isRecording) return null;

  // Recording starts on the first published track, so there is a short window
  // where it is expected but not yet running. Say so rather than claiming it.
  const pending = expected && !isRecording;

  return (
    <div
      role="status"
      className={cn(
        'recording-badge inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium whitespace-nowrap',
        pending
          ? 'is-pending border-border text-muted-foreground bg-white/[0.04]'
          : 'border-[color-mix(in_oklab,var(--destructive)_40%,transparent)] bg-[color-mix(in_oklab,var(--destructive)_16%,transparent)] text-[color-mix(in_oklab,var(--destructive)_78%,white)]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'recording-dot size-2 rounded-full bg-current',
          !pending && 'animate-(--animate-pulse-dot)',
        )}
      />
      <span className="hidden sm:inline">
        {pending ? 'Recording is about to start' : 'This call is being recorded'}
      </span>
      <span className="sm:hidden">{pending ? 'Rec soon' : 'Rec'}</span>
    </div>
  );
}
